import React, { useState, useEffect } from 'react';
import { RefreshCw, ChevronLeft, ChevronRight } from 'lucide-react';
import {
  format, startOfMonth, endOfMonth, eachDayOfInterval,
  isBefore, startOfDay, addMonths, subMonths,
} from 'date-fns';

function getBlockedDates() {
  try {
    return JSON.parse(localStorage.getItem('blockedDates')) || { manual: [], ical: [] };
  } catch { return { manual: [], ical: [] }; }
}

function setBlockedDates(data) {
  localStorage.setItem('blockedDates', JSON.stringify(data));
  window.dispatchEvent(new Event('storage'));
}

export default function AvailabilityTab() {
  const [viewMonth, setViewMonth] = useState(new Date());
  const [blocked, setBlocked] = useState(getBlockedDates());

  useEffect(() => {
    setBlocked(getBlockedDates());
  }, []);

  const isManuallyBlocked = (dateStr) => (blocked.manual || []).includes(dateStr);
  const isIcalBlocked = (dateStr) => (blocked.ical || []).includes(dateStr);

  const toggleDate = (dateStr) => {
    const current = getBlockedDates();
    const manualSet = new Set(current.manual || []);
    if (manualSet.has(dateStr)) {
      manualSet.delete(dateStr);
    } else {
      manualSet.add(dateStr);
    }
    const updated = { ...current, manual: [...manualSet] };
    setBlockedDates(updated);
    setBlocked(updated);
  };

  const generateDays = () => {
    const start = startOfMonth(viewMonth);
    const end = endOfMonth(viewMonth);
    const days = eachDayOfInterval({ start, end });
    const dow = start.getDay();
    const padding = dow === 0 ? 6 : dow - 1;
    return [...Array(padding).fill(null), ...days];
  };

  const today = startOfDay(new Date());
  const days = generateDays();

  return (
    <div className="admin-avail" data-testid="availability-tab">
      <div className="admin-avail-header">
        <h2>Availability Manager</h2>
      </div>

      <p className="admin-help-text">
        Click any future date to toggle between Available (white) and Blocked (red).
        Striped dates are synced from Airbnb iCal and refresh on each page load.
      </p>

      <div className="admin-cal-nav">
        <button onClick={() => setViewMonth(subMonths(viewMonth, 1))} className="admin-nav-btn">
          <ChevronLeft className="h-5 w-5" />
        </button>
        <h3>{format(viewMonth, 'MMMM yyyy')}</h3>
        <button onClick={() => setViewMonth(addMonths(viewMonth, 1))} className="admin-nav-btn">
          <ChevronRight className="h-5 w-5" />
        </button>
      </div>

      <div className="admin-cal-legend">
        <span><span className="legend-box available"></span> Available</span>
        <span><span className="legend-box manual-blocked"></span> Manually Blocked</span>
        <span><span className="legend-box ical-blocked"></span> Airbnb Blocked</span>
        <span><span className="legend-box past"></span> Past</span>
      </div>

      <div className="admin-cal-grid">
        {['Mon','Tue','Wed','Thu','Fri','Sat','Sun'].map(d => (
          <div key={d} className="admin-cal-weekday">{d}</div>
        ))}
        {days.map((date, i) => {
          if (!date) return <div key={`e-${i}`} className="admin-cal-day empty" />;
          const dateStr = format(date, 'yyyy-MM-dd');
          const isPast = isBefore(startOfDay(date), today);
          const isIcal = isIcalBlocked(dateStr);
          const isManual = isManuallyBlocked(dateStr);

          let cls = 'admin-cal-day';
          if (isPast) cls += ' past';
          else if (isIcal) cls += ' ical-blocked';
          else if (isManual) cls += ' manual-blocked';
          else cls += ' available';

          return (
            <button
              key={dateStr}
              className={cls}
              onClick={() => !isPast && !isIcal && toggleDate(dateStr)}
              disabled={isPast || isIcal}
              data-testid={`admin-day-${dateStr}`}
            >
              <span className="day-num">{format(date, 'd')}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
